import axios, { type InternalAxiosRequestConfig } from 'axios'

import { config } from '../config'
import { attachMicroserviceAuthRetry } from '../sso/interceptors/microservice-auth-retry.interceptor'
import { resolveDocumentsAccessToken } from '../sso/services/documents-access.service'
import { isSuiteSsoRedirectMode } from '../sso/sso-flags'
import { logWarn } from '../sso/utils/sso-logger'

function getStoredPayRefreshToken(): string {
  const raw = globalThis.sessionStorage.getItem('token') || globalThis.localStorage.getItem('token')
  return raw?.replaceAll('"', '') ?? ''
}

/**
 * Documents API client with automatic auth.
 * In SSO mode always uses identity JWT so existing requestAccessToken call sites keep working.
 */
export const DocumentsHttp = axios.create({
  timeout: 9000000,
  baseURL: config.api.documents.baseUrl,
  withCredentials: false
})

DocumentsHttp.interceptors.request.use(async (requestConfig: InternalAxiosRequestConfig) => {
  if (isSuiteSsoRedirectMode()) {
    const payAccessToken = await resolveDocumentsAccessToken(getStoredPayRefreshToken())
    requestConfig.headers.Authorization = `Bearer ${payAccessToken}`
    return requestConfig
  }

  const hasExplicitAuth =
    typeof requestConfig.headers?.Authorization === 'string' &&
    requestConfig.headers.Authorization.length > 0

  if (hasExplicitAuth) {
    return requestConfig
  }

  try {
    const accessToken = await resolveDocumentsAccessToken(getStoredPayRefreshToken())
    requestConfig.headers.Authorization = `Bearer ${accessToken}`
  } catch (error) {
    logWarn('DocumentsHttp — could not resolve documents access token', { error })
    throw error
  }

  return requestConfig
})

attachMicroserviceAuthRetry(DocumentsHttp)
